import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  withStyles,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import * as XLSX from "xlsx";
import products from "../../service/products";
import { parseDecimal } from "../../libs/format";
import { getTenant } from "../../libs/tenant";
import DialogError from "../alertas/DialogError";

const normalizeName = (name) => String(name || "").trim().toLowerCase();

// Colunas fixas do modelo (cabeçalho -> campo do produto).
const FIXED_COLUMNS = [
  { header: "Nome", field: "name" },
  { header: "Categoria", field: "category" },
  { header: "Apresentação", field: "presentation" },
  { header: "Unidade", field: "unit_of_measure" },
  { header: "Embalagem", field: "package_quantity" },
  { header: "Moeda", field: "currency" },
  { header: "Código", field: "code" },
];

// Importa produtos de uma planilha .xlsx para o grupo aberto, criando linha a linha.
// Props: group, priceTables[], close(), onImported().
class ImportProductsDialog extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      importing: false,
      done: false,
      total: 0,
      processed: 0,
      errors: [], // { line, message }
      showError: false,
      error: null,
    };
    this.fileInputRef = React.createRef();
  }

  downloadTemplate = () => {
    // Coluna única "Valor": no import ela cai na tabela de preço existente
    // (preferindo uma chamada "Valor") ou cria a tabela "Valor" se não houver.
    const headers = [...FIXED_COLUMNS.map((c) => c.header), "Valor"];
    const example = ["PROAGUAS 0,1%", "PROTEINADOS", "FAR", "kg", "30", "USD", "SKU-1", 21.85];
    const worksheet = XLSX.utils.aoa_to_sheet([headers, example]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Produtos");
    XLSX.writeFile(workbook, "modelo-produtos.xlsx");
  };

  // Resolve a tabela de preço destino para a coluna "Valor":
  // 1) tabela chamada "Valor"; 2) primeira tabela existente; 3) cria "Valor".
  resolveTargetTable = async () => {
    const tables = this.props.priceTables || [];
    const valorTable = tables.find((t) => normalizeName(t.name) === "valor");
    if (valorTable) return valorTable.id;
    if (tables.length > 0) return tables[0].id;
    const created = await products.createPriceTable({
      name: "Valor",
      product_group_id: this.props.group.id,
      position: 1,
    });
    return created.id;
  };

  handleFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target.result), {
          type: "array",
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        this.importRows(rows);
      } catch (err) {
        this.setState({
          showError: true,
          error: {
            title: "Planilha inválida",
            message: "Não foi possível ler o arquivo. Use o modelo .xlsx.",
          },
        });
      }
    };
    reader.readAsArrayBuffer(file);
    // permite reimportar o mesmo arquivo
    event.target.value = "";
  };

  rowToPayload = (row, targetTableId) => {
    const payload = { product_group_id: this.props.group.id };
    FIXED_COLUMNS.forEach((col) => {
      const value = row[col.header];
      if (value === "" || value === undefined || value === null) return;
      if (col.field === "package_quantity") {
        payload[col.field] = parseDecimal(value);
      } else {
        payload[col.field] = String(value).trim();
      }
    });
    const price = parseDecimal(row["Valor"]);
    if (price !== null && targetTableId) {
      payload.prices = [{ price_table_id: targetTableId, price }];
    }
    return payload;
  };

  importRows = async (rows) => {
    // Ignora linhas sem nome.
    const valid = rows.filter((r) => String(r["Nome"] || "").trim() !== "");
    if (valid.length === 0) {
      this.setState({
        showError: true,
        error: {
          title: "Nada para importar",
          message: 'Nenhuma linha com a coluna "Nome" preenchida.',
        },
      });
      return;
    }

    // Se a planilha tem preço na coluna "Valor", garante uma tabela de preço destino.
    let targetTableId = null;
    const sheetHasValor = valid.some((r) => parseDecimal(r["Valor"]) !== null);
    if (sheetHasValor) {
      try {
        targetTableId = await this.resolveTargetTable();
      } catch (e) {
        this.setState({
          showError: true,
          error: {
            title: "Erro ao preparar tabela",
            message: 'Não foi possível preparar a tabela de preço "Valor".',
          },
        });
        return;
      }
    }

    this.setState({
      importing: true,
      done: false,
      total: valid.length,
      processed: 0,
      errors: [],
    });

    // Carrega os produtos da empresa para reaproveitar os que já existem
    // (vincula ao catálogo + atualiza o preço) em vez de duplicar.
    const existingByName = {};
    try {
      const all = await products.listAllProducts(getTenant());
      (all || []).forEach((p) => {
        existingByName[normalizeName(p.name)] = p;
      });
    } catch (e) {
      // Sem a lista, segue criando todos (comportamento antigo).
    }

    const errors = [];
    for (let index = 0; index < valid.length; index++) {
      const row = valid[index];
      const payload = this.rowToPayload(row, targetTableId);
      const key = normalizeName(row["Nome"]);
      const existing = existingByName[key];
      try {
        if (existing) {
          // Produto já existe: vincula a este catálogo e faz upsert do preço.
          await products.attachProductToGroup(this.props.group.id, {
            product_id: existing.id,
            prices: payload.prices,
          });
        } else {
          const created = await products.createProduct(payload);
          // Permite reuso caso o mesmo nome se repita na planilha.
          if (created && created.id) existingByName[key] = created;
        }
      } catch (err) {
        errors.push({
          line: index + 2, // +1 cabeçalho, +1 base 1
          message:
            (err && err.data && err.data.message) ||
            `Falha ao importar "${row["Nome"]}".`,
        });
      }
      this.setState({ processed: index + 1 });
    }

    this.setState({ importing: false, done: true, errors });
    this.props.onImported();
  };

  render() {
    const { classes } = this.props;
    const { total, processed, importing, done, errors } = this.state;
    return (
      <Dialog open onClose={() => this.props.close()} maxWidth="sm" fullWidth>
        <DialogError
          open={this.state.showError}
          onClose={() => this.setState({ showError: false })}
          title={this.state.error ? this.state.error.title : "Erro"}
          message={this.state.error ? this.state.error.message : ""}
          suggestion={"Baixe o modelo e preencha conforme o exemplo."}
        />
        <DialogTitle>Importar produtos por planilha</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" paragraph>
            Baixe o modelo, preencha uma linha por produto (a coluna <b>Nome</b> é
            obrigatória) e informe o <b>Valor</b>. O valor entra na tabela de preço
            existente do catálogo (ou, se não houver nenhuma, uma tabela "Valor" é
            criada automaticamente). Se o produto já existir na empresa, ele é
            reaproveitado (vinculado a este catálogo) e só o preço é atualizado.
            Depois envie o arquivo .xlsx.
          </Typography>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<GetAppIcon />}
            onClick={this.downloadTemplate}
            className={classes.spaced}
          >
            Baixar modelo
          </Button>

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={this.fileInputRef}
            style={{ display: "none" }}
            onChange={this.handleFile}
          />
          <Button
            variant="contained"
            color="primary"
            disabled={importing}
            onClick={() => this.fileInputRef.current.click()}
          >
            Selecionar planilha
          </Button>

          {(importing || done) && (
            <div className={classes.progressBox}>
              <Typography variant="body2">
                {importing
                  ? `Importando ${processed}/${total}...`
                  : `Concluído: ${total - errors.length}/${total} importados` +
                    (errors.length ? `, ${errors.length} com erro` : "")}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={total ? (processed / total) * 100 : 0}
              />
            </div>
          )}

          {done && errors.length > 0 && (
            <List dense className={classes.errorList}>
              {errors.map((e) => (
                <ListItem key={e.line}>
                  <ListItemText primary={`Linha ${e.line}: ${e.message}`} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.props.close()} color="primary">
            {done ? "Fechar" : "Cancelar"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  spaced: {
    marginRight: theme.spacing(2),
  },
  progressBox: {
    marginTop: theme.spacing(3),
  },
  errorList: {
    marginTop: theme.spacing(1),
    maxHeight: 180,
    overflow: "auto",
    color: theme.palette.error.main,
  },
});

export default withStyles(styles)(ImportProductsDialog);
